/**
 * Canonical "nu vanaf" price for a deal or voucher.
 *
 * Mirrors the resolution chain in `DealCard.actualPrice` so every
 * customer-facing surface (homepage, category, offer, search dropdown,
 * voucher detail hero, voucher listing/category cards, thank-you
 * cross-sell) shows the same number for the same deal/voucher.
 *
 * Priority:
 *   1. fixed_display_price / fixedDisplayPrice  (operator-anchored)
 *   2. fixed_voucher_price / fixedVoucherPrice  (legacy name, pre-
 *      2026-05-12 rename — backend dual-emits during transition)
 *   3. For voucher deals (is_voucher_deal truthy) OR when the caller
 *      passes `{ voucherRow: true }` for catalogue-voucher rows that
 *      don't carry `is_voucher_deal` (voucher-list/category BFF
 *      payloads are implicitly all vouchers):
 *        value_cents → selling_price → value → selling_round_off_price →
 *        lowest_price → lowestPrice → actual_price → discounted_price →
 *        lowest_price_before_discount → price_before_discount
 *   4. For non-voucher deals:
 *        calendar_lowest_price → lowest_price →
 *        lowest_price_before_discount → price_before_discount →
 *        selling_price
 *
 * `value`, `selling_round_off_price`, `actual_price`, `discounted_price`
 * are legacy voucher-row aliases carried over from the six hand-copied
 * FE chains this function replaces (C21, Fable5 fix-campagne) so routing
 * every surface through here is a behavior-preserving refactor, not a
 * price change.
 *
 * Per CLAUDE.md HARD RULE `feedback_price_alignment_hard_rule.md`:
 * across all 99 non-voucher deals + vouchers with anchors, the
 * "nu vanaf" on home/cat/offer MUST equal the displayed search-bar
 * dropdown price. ACM + customer-trust.
 *
 * Returns 0 when no price field is populated.
 */
import { isVoucherDeal as isCanonicalVoucherDeal } from "@/lib/is-voucher-deal";

export function resolveDisplayPrice(deal, { voucherRow = false } = {}) {
  if (!deal || typeof deal !== "object") return 0;

  const fixed =
    Number(deal.fixed_display_price) ||
    Number(deal.fixedDisplayPrice) ||
    Number(deal.fixed_voucher_price) ||
    Number(deal.fixedVoucherPrice) ||
    0;
  if (fixed > 0) return fixed;

  // Defensive hardening, 2026-08-15 (found alongside DealCard.js and
  // page.js's related-deals filter while investigating a reported
  // reserveringskosten-hint leak that turned out to be a misreading — see
  // those two files' comments; not a confirmed live bug here either). This
  // was a THIRD local `=== "true" || === true` hand-copy of is_voucher_deal
  // detection. Worth closing even though no live door has been verified to
  // emit the numeric/"1" forms today (checked package/allPackage/voucher
  // doors), because the failure mode here is more than cosmetic if it were
  // ever hit: a mis-detected record would resolve through the WRONG branch
  // below (calendar_lowest_price/lowest_price instead of value_cents/
  // selling_price), and every surface this shared resolver feeds (per the
  // file doc-comment: homepage, category, offer, search dropdown, voucher
  // hero, voucher cards, cross-sell) would show a wrong PRICE NUMBER for
  // such a record, not just a wrong caption. Canonical helper as
  // drift-prevention so this can't diverge from the other call sites.
  const isVoucherDeal = voucherRow || isCanonicalVoucherDeal(deal);

  if (isVoucherDeal) {
    return (
      Number(deal.value_cents) ||
      Number(deal.selling_price) ||
      Number(deal.value) ||
      Number(deal.selling_round_off_price) ||
      Number(deal.lowest_price) ||
      Number(deal.lowestPrice) ||
      Number(deal.actual_price) ||
      Number(deal.discounted_price) ||
      Number(deal.lowest_price_before_discount) ||
      Number(deal.price_before_discount) ||
      0
    );
  }

  return (
    Number(deal.calendar_lowest_price) ||
    Number(deal.lowest_price) ||
    Number(deal.lowestPrice) ||
    Number(deal.lowest_price_before_discount) ||
    Number(deal.price_before_discount) ||
    Number(deal.selling_price) ||
    0
  );
}
